import { ref } from "vue";
import { API } from "@/api/api";
import { Response } from "@/api/client";
import i18n from "@/plugins/i18n";
import { useError } from "@/composables/useError";
import { useFilters } from "@/composables/useFilters";
import type { CreateOrderExpense, OrderExpense, UpdateOrderExpense } from "@/api/orders/dto/order.dto";

const t = i18n.global.t;

export function useOrderExpenses() {
  const api = new API();
  const { showAPIError } = useError();
  const { money } = useFilters();

  const isLoading = ref(false);

  function createOrderExpenseDraft(): CreateOrderExpense {
    return { type: null, rate: null, note: null, affectsDispatcherProfit: true };
  }

  function formatOrderExpenseRate(rate: number): string {
    return money(rate);
  }

  async function createOrderExpense(orderId: string, expense: CreateOrderExpense): Promise<OrderExpense | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.createOrderExpense(orderId, expense);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-expenses.create-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-expenses.create-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateOrderExpense(
    orderId: string,
    expenseId: number,
    expense: UpdateOrderExpense,
  ): Promise<OrderExpense | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.updateOrderExpense(orderId, expenseId, expense);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-expenses.update-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-expenses.update-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteOrderExpense(orderId: string, expenseId: number): Promise<boolean> {
    isLoading.value = true;

    try {
      const response = await api.orders.deleteOrderExpense(orderId, expenseId);

      if (!response.success) {
        showAPIError(response, t("order-expenses.delete-error"));
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-expenses.delete-error"));
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    createOrderExpense,
    updateOrderExpense,
    deleteOrderExpense,
    createOrderExpenseDraft,
    formatOrderExpenseRate,
  };
}
